import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { FileText, AlertCircle, ArrowLeft, Loader2, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { createClient } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { OrganizationEntityType, ClientEntityType, BankAccountEntityType, PatientEntityType, PractitionerEntityType } from "@/lib/supabase/supabaseTypes";
import { PdfTemplateType } from "../../../pdf-templates/document-template.types";
import { OrganizationSelector } from "./components/organization-selector.component";
import { PdfTypeSelector, ESSENTIAL_TYPES, ALL_PDF_TYPES } from "./components/pdf-type-selector.component";
import { PdfGridView } from "./components/pdf-grid-view.component";
import { ClientSettingsFilter, ClientSettingField, ALL_FIELDS } from "./components/client-settings-filter.component";
import { useGeneratePdfs } from "./hooks/useGeneratePdfs";
import { clientExample } from "../../../constants/clientExample";

// Mapping of which client settings affect which PDF types
const SETTING_TO_PDF_TYPES: Record<ClientSettingField, PdfTemplateType[]> = {
	// Tax/VAT settings affect all invoice-related documents
	vat_display_override: [
		PdfTemplateType.LIEFERSCHEIN,
		PdfTemplateType.RECHNUNG,
		PdfTemplateType.SAMMELRECHNUNG,
	],
	patient_name_format: [
		PdfTemplateType.SAMMELRECHNUNG,
	],
	// Invoice-specific settings
	is_inv_w_job_title: [
		PdfTemplateType.SAMMELRECHNUNG
	],
	is_inv_w_pat_data: [
		PdfTemplateType.SAMMELRECHNUNG,
	],
	// Sammelrechnung-specific settings
	srg_sorting_option: [PdfTemplateType.SAMMELRECHNUNG],
	is_srg_grouped_by_prac: [PdfTemplateType.SAMMELRECHNUNG],
	is_show_insurance_on_pdf: [
		PdfTemplateType.LIEFERSCHEIN,
		PdfTemplateType.KOSTENVORANSCHLAG,
	],
};
import { jobExample } from "../../../constants/jobExample";
import { jobDocumentExample } from "../../../constants/jobDocumentExample";
import { jobItemExamples } from "../../../constants/jobItemExample";
import { patientExample } from "../../../constants/patientExample";
import { practitionerExample } from "../../../constants/practitionerExample";
import { bankAccountExample } from "../../../constants/bankAccountExample";
import { PdfTemplateOptions } from "../../pdf-template-configuration/pdf-selected-template.config";
import { calculateJobTotalsLegacy, calculateSammelrechnungRowsAndTotals, ExtendedJobDocument } from "../../../lib/utils/calculate";
import { PaymentSlipDataInput } from "../../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { JobDocumentTypeEnum } from "../../job-page/job-document/job-document.types";
import { convertLegacyPdfTemplateConfiguration, PdfTemplateConfiguration, DEFAULT_TEMPLATE_CONFIGURATION } from "../../../context/PdfTemplateConfigurationContext";
import { isDeveloper } from "@/lib/utils/is-app-user";

const SUPABASE_URL: string = `https://${import.meta.env.VITE_PROD_SUPABASE_PROJECT_URL}.supabase.co`;
const SUPABASE_SERVICE_ROLE_KEY: string = import.meta.env.VITE_PROD_SUPABASE_SERVICE_ROLE_KEY;


interface PdfTestData {
	organization_id: string;
	organization: OrganizationEntityType;
	sample_clients: ClientEntityType[];
	sample_bank_account: BankAccountEntityType | null;
	sample_patient: PatientEntityType | null;
	sample_practitioner: PractitionerEntityType | null;
}

interface EstimationResult {
	totalPdfs: number;
	estimatedTimeSeconds: number;
	sampleCount: number;
	sampleTimeSeconds: number;
}

export const PdfExportPage = () => {
	const navigate = useNavigate();
	const [testData, setTestData] = useState<PdfTestData[]>([]);
	const [pdfTestData, setPdfTestData] = useState<PdfTestData[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [estimating, setEstimating] = useState(false);
	const [estimation, setEstimation] = useState<EstimationResult | null>(null);

	if (!SUPABASE_SERVICE_ROLE_KEY) {
		return (
			<div className="flex flex-col px-12 py-6">
				<HeaderPortal>
					<PageTitle
						title={[{ name: "Support", link: "/support" }, { name: "Zugriff verweigert" }]}
						icon={<AlertCircle width="16px" height="16px" color="rgb(220, 38, 38)" />}
					/>
				</HeaderPortal>
				<div className="flex flex-col items-center justify-center py-12">	
					<AlertCircle className="w-16 h-16 text-red-500 mb-4" />
					<h2 className="text-xl font-semibold text-gray-900 mb-2">
						Zugriff verweigert
					</h2>
					<p className="text-gray-600">
						Der Service Role Key ist nicht definiert.
					</p>
				</div>
			</div>
		);
	}

	const supabase = createClient(SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY);


	// Selection state
	const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
	const [selectedPdfTypes, setSelectedPdfTypes] = useState<PdfTemplateType[]>([
		PdfTemplateType.KOSTENVORANSCHLAG,
		PdfTemplateType.LIEFERSCHEIN,
		PdfTemplateType.RECHNUNG,
		PdfTemplateType.SAMMELRECHNUNG,
	]);
	const [selectedClientFields, setSelectedClientFields] = useState<ClientSettingField[]>([]);

	// PDF generation
	const { generatedPdfs, generating, progress, error: genError, generateAll, clearAll } = useGeneratePdfs();

	// Fetch organizations on mount
	useEffect(() => {
		const fetchOrganizations = async () => {
			try {
				setLoading(true);
				const { data, error } = await supabase
					.rpc('get_active_lab_organizations', { days_ago: 60 });

				if (error) throw error;

				// Transform to PdfTestData format with empty sample_clients
				const transformedData: PdfTestData[] = (data || []).map((org: any) => ({
					organization_id: org.id,
					organization: {
						id: org.id,
						name: org.name,
						street: org.street,
						city: org.city,
						postal_code: org.postal_code,
						country: org.country,
					} as OrganizationEntityType,
					sample_clients: [],
					sample_bank_account: null,
					sample_patient: null,
					sample_practitioner: null,
				}));

				setTestData(transformedData);
			} catch (err) {
				console.error('Error fetching organizations:', err);
				setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
			} finally {
				setLoading(false);
			}
		};

		if (window.location.hostname === "localhost") {
			fetchOrganizations();
		}
	}, []);

	// Get relevant filter fields for a specific PDF type
	const getRelevantFilters = (pdfType: PdfTemplateType): ClientSettingField[] => {
		if (selectedClientFields.length === 0) return [];

		return selectedClientFields.filter(field =>
			SETTING_TO_PDF_TYPES[field]?.includes(pdfType)
		);
	};

	// Filter clients based on relevant settings for a PDF type
	const filterClientsForPdfType = (
		clients: ClientEntityType[],
		pdfType: PdfTemplateType
	): ClientEntityType[] => {
		const relevantFilters = getRelevantFilters(pdfType);

		// No relevant filters: return one client (prefer with practitioner)
		if (relevantFilters.length === 0) {
			return clients.slice(0, 1);
		}

		// Filter clients for unique combinations of relevant fields
		const seen = new Set<string>();
		const uniqueClients: ClientEntityType[] = [];

		for (const client of clients) {
			const key = relevantFilters
				.map(field => {
					const value = client[field];
					return value === null || value === undefined ? 'null' : String(value);
				})
				.join('::');

			if (!seen.has(key)) {
				seen.add(key);
				uniqueClients.push(client);
			}
		}

		return uniqueClients;
	};

	const handleEstimate = async () => {
		try {
			setEstimating(true);
			setError(null);
			setEstimation(null);

			// Fetch full PDF test data without filters (get all clients)
			const { data, error } = await supabase
				.rpc('get_pdf_test_data', {
					org_ids: selectedOrgIds,
					filter_fields: [], // Get all clients
				});

			if (error) throw error;

			// Parse the JSONB result
			const fetchedPdfTestData: PdfTestData[] = data as PdfTestData[];
			setPdfTestData(fetchedPdfTestData);

			// Calculate total PDFs by filtering clients per PDF type
			let totalPdfs = 0;
			for (const org of fetchedPdfTestData) {
				for (const pdfType of selectedPdfTypes) {
					const filteredClients = filterClientsForPdfType(org.sample_clients, pdfType);
					totalPdfs += filteredClients.length;
				}
			}

			if (totalPdfs === 0) {
				setError('Keine PDFs zum Generieren gefunden');
				return;
			}

			// Generate 5 sample PDFs to estimate time (use first org, first few types)
			const sampleCount = Math.min(4, totalPdfs);
			const firstOrg = fetchedPdfTestData[0];
			let sampledPdfs = 0;
			const sampleOrgData: PdfTestData[] = [];

			// Collect enough PDFs for sampling
			for (const pdfType of selectedPdfTypes) {
				if (sampledPdfs >= sampleCount) break;

				const filteredClients = filterClientsForPdfType(firstOrg.sample_clients, pdfType);
				const clientsToUse = filteredClients.slice(0, Math.min(filteredClients.length, sampleCount - sampledPdfs));

				if (clientsToUse.length > 0 && sampleOrgData.length === 0) {
					// Add org with filtered clients for this type
					sampleOrgData.push({
						...firstOrg,
						sample_clients: clientsToUse,
					});
				}

				sampledPdfs += clientsToUse.length;
			}

			const startTime = performance.now();
			await generateAll(sampleOrgData, selectedPdfTypes, SETTING_TO_PDF_TYPES, selectedClientFields);
			const endTime = performance.now();

			const sampleTimeSeconds = (endTime - startTime) / 1000;
			const avgTimePerPdf = sampledPdfs > 0 ? sampleTimeSeconds / sampledPdfs : sampleTimeSeconds;
			const estimatedTotalSeconds = avgTimePerPdf * totalPdfs;

			setEstimation({
				totalPdfs,
				estimatedTimeSeconds: estimatedTotalSeconds,
				sampleCount,
				sampleTimeSeconds,
			});

			// Clear generated PDFs from sample
			clearAll();
		} catch (err) {
			console.error('Error estimating PDFs:', err);
			setError(err instanceof Error ? err.message : 'Fehler bei der Schätzung');
		} finally {
			setEstimating(false);
		}
	};

	const handleGenerate = async () => {
		try {
			// Fetch full PDF test data without filters (get all clients)
			const { data, error } = await supabase
				.rpc('get_pdf_test_data', {
					org_ids: selectedOrgIds,
					filter_fields: [], // Get all clients
				});

			if (error) throw error;

			// Parse the JSONB result
			const fetchedPdfTestData: PdfTestData[] = data as PdfTestData[];
			setPdfTestData(fetchedPdfTestData);

			// Create a structure that represents all PDFs that need to be generated
			// For each org, we need to track which clients apply to which PDF types
			// Since different PDF types may need different clients, we need to create
			// virtual "org instances" per PDF type with the appropriate filtered clients

			const orgPdfTypePairs: Array<{ orgData: PdfTestData; pdfType: PdfTemplateType }> = [];

			for (const org of fetchedPdfTestData) {
				for (const pdfType of selectedPdfTypes) {
					const filteredClients = filterClientsForPdfType(org.sample_clients, pdfType);
					if (filteredClients.length > 0) {
						orgPdfTypePairs.push({
							orgData: {
								...org,
								sample_clients: filteredClients,
							},
							pdfType,
						});
					}
				}
			}

			await generateAll(fetchedPdfTestData, selectedPdfTypes, SETTING_TO_PDF_TYPES, selectedClientFields);
		} catch (err) {
			console.error('Error fetching PDF test data:', err);
			setError(err instanceof Error ? err.message : 'Fehler beim Laden der PDF-Daten');
		}
	};

	const canGenerate = selectedOrgIds.length > 0 && selectedPdfTypes.length > 0 && !generating;
	const canEstimate = selectedOrgIds.length > 0 && selectedPdfTypes.length > 0 && !generating && !estimating;

	// Preset configuration handlers
	const handlePresetEssentialsPure = () => {
		setSelectedOrgIds(testData.map(org => org.organization_id));
		setSelectedPdfTypes(ESSENTIAL_TYPES);
		setSelectedClientFields([]);
	};

	const handlePresetEssentialsAll = () => {
		setSelectedOrgIds(testData.map(org => org.organization_id));
		setSelectedPdfTypes(ESSENTIAL_TYPES);
		setSelectedClientFields(ALL_FIELDS);
	};

	const handlePresetAll = () => {
		setSelectedOrgIds(testData.map(org => org.organization_id));
		setSelectedPdfTypes(ALL_PDF_TYPES);
		setSelectedClientFields(ALL_FIELDS);
	};

	// Localhost-only guard
	if (!isDeveloper(import.meta.env)) {
		return (
			<div className="flex flex-col px-12 py-6">
				<HeaderPortal>
					<PageTitle
						title={[{ name: "Support", link: "/support" }, { name: "Zugriff verweigert" }]}
						icon={<AlertCircle width="16px" height="16px" color="rgb(220, 38, 38)" />}
					/>
				</HeaderPortal>
				<div className="flex flex-col items-center justify-center py-12">
					<AlertCircle className="w-16 h-16 text-red-500 mb-4" />
					<h2 className="text-xl font-semibold text-gray-900 mb-2">
						Nur auf Localhost verfügbar
					</h2>
					<p className="text-gray-600 mb-6">
						Diese Seite ist nur auf localhost zugänglich.
					</p>
					<Button
						onClick={() => navigate("/support")}
						variant="outline"
					>
						<ArrowLeft className="w-4 h-4 mr-2" />
						Zurück zu Support
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col px-12 py-6">
			<HeaderPortal>
				<PageTitle
					title={[{ name: "Support", link: "/support" }, { name: "PDF Export" }]}
					icon={<FileText width="16px" height="16px" color="rgb(55, 65, 81)" />}
				/>
			</HeaderPortal>

			<div className="flex flex-col gap-6 py-4">
				<div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
					<div className="flex items-start gap-3">
						<AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
						<div>
							<h3 className="font-medium text-orange-900">Development Tool</h3>
							<p className="text-orange-800 text-sm mt-1">
								Diese Seite verwendet den Supabase Service Role Key und ist nur für
								Entwicklungszwecke auf localhost verfügbar.
							</p>
						</div>
					</div>
				</div>

				{loading && (
					<div className="flex items-center justify-center py-12">
						<Loader2 className="w-8 h-8 animate-spin text-blue-600" />
						<span className="ml-3 text-gray-600">Lade Organisationen...</span>
					</div>
				)}

				{error && (
					<div className="bg-red-50 border border-red-200 rounded-lg p-4">
						<div className="flex items-start gap-3">
							<AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
							<div>
								<h3 className="font-medium text-red-900">Fehler beim Laden</h3>
								<p className="text-red-800 text-sm mt-1">{error}</p>
							</div>
						</div>
					</div>
				)}

				{!loading && (
					<>
						{/* Organization Selector */}
						<OrganizationSelector
							organizations={testData}
							selectedIds={selectedOrgIds}
							onSelectionChange={setSelectedOrgIds}
						/>

						{/* PDF Type Selector */}
						<PdfTypeSelector
							selectedTypes={selectedPdfTypes}
							onSelectionChange={setSelectedPdfTypes}
						/>

						{/* Client Settings Filter */}
						<ClientSettingsFilter
							selectedFields={selectedClientFields}
							onFieldsChange={setSelectedClientFields}
						/>

						{/* Preset Configuration Buttons */}
						<div className="bg-white border border-gray-200 rounded-lg p-6">
							<h3 className="font-semibold text-gray-900 mb-4">Schnellauswahl</h3>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
								<button
									onClick={handlePresetEssentialsPure}
									className="border-2 border-gray-300 rounded-lg p-4 text-left hover:border-blue-500 hover:bg-blue-50 transition-colors"
									disabled={generating || estimating}
								>
									<h4 className="font-medium text-gray-900 mb-2">Essentials Pure</h4>
									<p className="text-sm text-gray-600 mb-3">
										Grundlegende PDF-Typen ohne Einstellungsfilter
									</p>
									<div className="text-xs text-gray-500 space-y-1">
										<div>• Alle Organisationen</div>
										<div>• 4 PDF-Typen (KV, LS, RG, SRG)</div>
										<div>• Keine Einstellungsfilter</div>
									</div>
								</button>

								<button
									onClick={handlePresetEssentialsAll}
									className="border-2 border-gray-300 rounded-lg p-4 text-left hover:border-blue-500 hover:bg-blue-50 transition-colors"
									disabled={generating || estimating}
								>
									<h4 className="font-medium text-gray-900 mb-2">Essentials All</h4>
									<p className="text-sm text-gray-600 mb-3">
										Grundlegende PDF-Typen mit allen Einstellungsfiltern
									</p>
									<div className="text-xs text-gray-500 space-y-1">
										<div>• Alle Organisationen</div>
										<div>• 4 PDF-Typen (KV, LS, RG, SRG)</div>
										<div>• Alle Einstellungsfilter ({ALL_FIELDS.length})</div>
									</div>
								</button>

								<button
									onClick={handlePresetAll}
									className="border-2 border-gray-300 rounded-lg p-4 text-left hover:border-blue-500 hover:bg-blue-50 transition-colors"
									disabled={generating || estimating}
								>
									<h4 className="font-medium text-gray-900 mb-2">Alle</h4>
									<p className="text-sm text-gray-600 mb-3">
										Maximale Abdeckung: alle Typen und Einstellungen
									</p>
									<div className="text-xs text-gray-500 space-y-1">
										<div>• Alle Organisationen</div>
										<div>• Alle PDF-Typen ({ALL_PDF_TYPES.length})</div>
										<div>• Alle Einstellungsfilter ({ALL_FIELDS.length})</div>
									</div>
								</button>
							</div>
						</div>

					{/* Generate Button */}
						<div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-6">
							<div>
								<h3 className="font-medium text-gray-900">PDFs generieren</h3>
								<p className="text-sm text-gray-600 mt-1">
									{selectedOrgIds.length} Organisation(en) × {selectedPdfTypes.length} PDF-Typ(en)
								</p>
							</div>
							<div className="flex items-center gap-3">
								<Button
									onClick={handleEstimate}
									disabled={!canEstimate}
									variant="outline"
								>
									<Loader2 className={`w-4 h-4 mr-2 ${estimating ? 'animate-spin' : ''}`} />
									{estimating ? 'Berechne...' : 'Menge berechnen'}
								</Button>
								{estimation && (
									<div className="flex items-center gap-4 text-sm">
										<div>
											<span className="font-semibold text-gray-900">{estimation.totalPdfs}</span>
											<span className="text-gray-600 ml-1">PDFs</span>
										</div>
										<div>
											<span className="font-semibold text-gray-900">
												{Math.floor(estimation.estimatedTimeSeconds / 60)}:{String(Math.floor(estimation.estimatedTimeSeconds % 60)).padStart(2, '0')}
											</span>
											<span className="text-gray-600 ml-1">geschätzt</span>
										</div>
									</div>
								)}
								{generatedPdfs.length > 0 && (
									<Button
										onClick={clearAll}
										variant="outline"
										disabled={generating}
									>
										Zurücksetzen
									</Button>
								)}
								<Button
									onClick={handleGenerate}
									disabled={!canGenerate}
								>
									<Play className="w-4 h-4 mr-2" />
									{generating ? 'Generiere...' : 'PDFs generieren'}
								</Button>
							</div>
						</div>

						{/* Progress Indicator */}
						{generating && progress && (
							<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
								<div className="flex items-start gap-3">
									<Loader2 className="w-5 h-5 text-blue-600 animate-spin mt-0.5" />
									<div className="flex-1">
										<h3 className="font-medium text-blue-900">Generiere PDFs...</h3>
										<p className="text-blue-800 text-sm mt-1">
											{progress.currentOrg} - {progress.currentType} ({progress.current}/{progress.total})
										</p>
										{progress.estimatedRemainingMs !== undefined && (
											<p className="text-xs text-blue-700 mt-1">
												ca. {Math.floor(progress.estimatedRemainingMs / 60000)}:{String(Math.floor((progress.estimatedRemainingMs % 60000) / 1000)).padStart(2, '0')} verbleibend
											</p>
										)}
										<div className="mt-2 bg-blue-200 rounded-full h-2">
											<div
												className="bg-blue-600 h-2 rounded-full transition-all"
												style={{ width: `${(progress.current / progress.total) * 100}%` }}
											/>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* Generation Error */}
						{genError && (
							<div className="bg-red-50 border border-red-200 rounded-lg p-4">
								<div className="flex items-start gap-3">
									<AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
									<div>
										<h3 className="font-medium text-red-900">Fehler bei der Generierung</h3>
										<p className="text-red-800 text-sm mt-1">{genError}</p>
									</div>
								</div>
							</div>
						)}

						{/* Generated PDFs Grid */}
						<PdfGridView pdfs={generatedPdfs} />

						{/* Hidden rendering area for PDFs */}
						<div style={{ position: 'absolute', left: '-10000px', top: 0 }}>
							{pdfTestData.flatMap(orgData => {
								if (!orgData || orgData.sample_clients.length === 0) return [];

								// For each PDF type, filter clients based on relevant settings
								return selectedPdfTypes.flatMap(pdfType => {
									const filteredClients = filterClientsForPdfType(orgData.sample_clients, pdfType);

									return filteredClients.map(client => {
										const bankAccount = orgData.sample_bank_account || bankAccountExample;
										const patient = orgData.sample_patient || patientExample;
										const practitioner = orgData.sample_practitioner || practitionerExample;

										// Parse PDF configuration
										let pdfTemplateConfiguration: PdfTemplateConfiguration;
										try {
											let rawConfig;

											// documents_config is JSONB in database, so it comes as an object already
											if (typeof orgData.organization.documents_config === 'object' && orgData.organization.documents_config !== null) {
												rawConfig = orgData.organization.documents_config as any;
											} else if (typeof orgData.organization.documents_config === 'string') {
												// In case it's stored as a string, parse it
												rawConfig = JSON.parse(orgData.organization.documents_config);
											} else {
												// Fallback to default config
												rawConfig = DEFAULT_TEMPLATE_CONFIGURATION;
											}

											// Convert legacy configuration if needed
											pdfTemplateConfiguration = convertLegacyPdfTemplateConfiguration(rawConfig as PdfTemplateConfiguration);
										} catch (e) {
											console.warn('Failed to parse documents_config:', e);
											pdfTemplateConfiguration = DEFAULT_TEMPLATE_CONFIGURATION;
										}

										// Calculate job totals
										const jobTotals = calculateJobTotalsLegacy({
											job: jobExample,
											jobDocument: jobDocumentExample[pdfType],
											jobItems: jobItemExamples,
											organization: orgData.organization,
											client,
										});

										// Prepare payment slip data
										const paymentSlipData: PaymentSlipDataInput = {
											bankAccount: bankAccount,
											recipient: client,
											amount: jobTotals.total.text,
											currency: jobExample.currency_text || "",
											invoiceNumber: "000001",
										};

										// Prepare Sammelrechnung data
										const extendedJobDocumentsExample: ExtendedJobDocument[] = Array.from(
											{ length: 3 },
											(_, i) => i
										).map(() => ({
											job_client_id: client.id,
											job_code: jobExample.code,
											job_document_date: jobDocumentExample[pdfType].date,
											job_document_discount_material: jobDocumentExample[pdfType].discount_material,
											job_document_discount_work: jobDocumentExample[pdfType].discount_work,
											job_document_id: jobDocumentExample[pdfType].id,
											job_items: jobItemExamples.map(item => ({
												id: item.id,
												price: item.price,
												quantity: item.quantity,
												tp_value: item.tp_value,
												discount: item.discount,
												is_req_dr_tax: item.is_req_dr_tax || false,
												is_warranty: item.is_warranty || false,
												type: item.type,
												code: "112",
												code_e: "112",
												description: "description",
												note: ""
											})),
											job_title: jobExample.title,
											organization_id: orgData.organization.id,
											patient_code: patient.code,
											patient_first_name: patient.first_name,
											patient_last_name: patient.last_name,
											patient_title: patient.title,
											practitioner_id: practitioner.id,
											practitioner_first_name: practitioner.first_name,
											practitioner_last_name: practitioner.last_name,
											practitioner_title: practitioner.title,
											job_document_type: JobDocumentTypeEnum.DeliveryNote,
											is_ignore_monthly_discount: false,
											job_tax_rate: jobExample.tax_rate,
											patient_birth_date: patient.birth_date,
											job_document_amount: jobDocumentExample[pdfType].amount,
											job_document_code: jobDocumentExample[pdfType].code,
											job_document_created_at: jobDocumentExample[pdfType].created_at,
											job_document_created_by: jobDocumentExample[pdfType].created_by,
											job_document_deleted_at: jobDocumentExample[pdfType].deleted_at,
											job_document_fae_id: jobDocumentExample[pdfType].fae_id,
											job_document_job_id: jobDocumentExample[pdfType].job_id,
											job_document_work_title: jobDocumentExample[pdfType].work_title,
											job_document_modified_at: jobDocumentExample[pdfType].modified_at,
											job_document_modified_by: jobDocumentExample[pdfType].modified_by,
											job_document_note: jobDocumentExample[pdfType].note,
											job_document_status: jobDocumentExample[pdfType].status,
											job_document_title: jobDocumentExample[pdfType].title,
											job_document_type_data: jobDocumentExample[pdfType].type_data,
											job_currency_text: jobExample.currency_text,
											job_round_to: jobExample.round_to,
										} as ExtendedJobDocument));

										const mwstPercentage = extendedJobDocumentsExample[0].job_tax_rate ?? 0;
										const sammelrechnungCalculationResult = calculateSammelrechnungRowsAndTotals(
											extendedJobDocumentsExample,
											orgData.organization,
											client,
											{ [client.id as string]: client },
											mwstPercentage
										);

										return (
											<div key={`${orgData.organization_id}-${client.id}-${pdfType}`} id={`pdf-${orgData.organization_id}-${client.id}-${pdfType}`} className="pdf-preview">
												<PdfTemplateOptions
													organization={orgData.organization}
													pdfTemplateConfiguration={pdfTemplateConfiguration}
													client={client}
													jobExample={jobExample}
													jobItems={jobItemExamples}
													jobDocumentExample={jobDocumentExample[pdfType]}
													patientExample={patient}
													practitionerExample={practitioner}
													jobTotals={jobTotals}
													paymentSlipData={paymentSlipData}
													sammelrechnungCalculationResult={sammelrechnungCalculationResult}
													selectedPdfTemplate={pdfType}
													clientAvatarUrl={undefined}
												/>
											</div>
										);
									})
								});
							})}
						</div>
					</>
				)}
			</div>
		</div>
	);
};
